import React from 'react';

export interface GtLegendItem {
    name: string;
    color: string;
    /** null for pie/donut slices */
    total: number | null;
}

interface GtChartLegendProps {
    items: GtLegendItem[];
    pos: 'bottom' | 'top' | 'right';
    isDark: boolean;
    labelColor: string;
    isLineOrArea: boolean;
    isStacked?: boolean;
    stackGroups?: number[][];
    stackGroupLabels?: string[];
    /** Multiplier applied to swatch sizes and font sizes. Default: 1 */
    legendScale?: number;
}

const fmtTotal = (v: number) =>
    v >= 1_000_000 ? `${+(v / 1_000_000).toFixed(1)}M`
        : v >= 1_000 ? `${+(v / 1_000).toFixed(1)}K`
            : String(v);

export function GtChartLegend({
    items, pos, isDark, isLineOrArea, isStacked, stackGroups, stackGroupLabels,
    legendScale = 1,
}: GtChartLegendProps) {
    const D = isDark;
    const s = legendScale;

    const grandTotal = isStacked && items.every(i => i.total !== null)
        ? items.reduce((acc, i) => acc + (i.total ?? 0), 0)
        : 0;

    // ── Shared swatch ─────────────────────────────────────────────────────────
    const Swatch = ({ color }: { color: string }) => (
        <span style={{
            display: 'inline-block',
            width: isLineOrArea ? 10 * s : 6 * s,
            height: isLineOrArea ? 1.5 * s : 6 * s,
            borderRadius: isLineOrArea ? 99 : 1.5 * s,
            backgroundColor: color,
            flexShrink: 0,
        }} />
    );

    // ── Single legend item ────────────────────────────────────────────────────
    const LegendPill = ({ item }: { item: GtLegendItem }) => {
        const pct = isStacked && grandTotal > 0 && item.total !== null
            ? Math.round((item.total / grandTotal) * 100)
            : null;

        return (
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4 * s,
            }}>
                <Swatch color={item.color} />
                <span style={{
                    fontSize: 10 * s,
                    fontWeight: 400,
                    color: D ? '#7d8fa5' : '#64748b',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.01em',
                }}>
                    {item.name}
                </span>
                {item.total !== null && (
                    <span style={{
                        fontSize: 10.5 * s,
                        fontWeight: 700,
                        color: item.color,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                    }}>
                        {fmtTotal(item.total)}
                    </span>
                )}
                {pct !== null && (
                    <span style={{
                        fontSize: 9 * s,
                        fontWeight: 500,
                        color: D ? '#3d5068' : '#b0bec5',
                        fontVariantNumeric: 'tabular-nums',
                    }}>
                        {pct}%
                    </span>
                )}
            </div>
        );
    };

    // ── Bottom legend ─────────────────────────────────────────────────────────
    if (pos === 'bottom') {
        const sepColor = D ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
        const divColor = D ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.09)';

        type Node =
            | { kind: 'item'; item: GtLegendItem; idx: number }
            | { kind: 'divider'; label?: string };

        const nodes: Node[] = [];
        if (stackGroups?.length) {
            stackGroups.forEach((grp, gi) => {
                if (gi > 0) nodes.push({ kind: 'divider', label: stackGroupLabels?.[gi] });
                grp.forEach((si) => {
                    if (items[si]) nodes.push({ kind: 'item', item: items[si], idx: si });
                });
            });
        } else {
            items.forEach((item, idx) => nodes.push({ kind: 'item', item, idx }));
        }

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                rowGap: 3,
                columnGap: 10,
                borderTop: `1px solid ${sepColor}`,
                padding: '4px 8px',
                fontFamily: 'inherit',
            }}>
                {nodes.map((node, ni) => {
                    if (node.kind === 'divider') {
                        return (
                            <span key={`div-${ni}`} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                margin: '0 2px',
                            }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: 1,
                                    height: 12,
                                    background: divColor,
                                    borderRadius: 99,
                                    flexShrink: 0,
                                    opacity: 0.6,
                                }} />
                            </span>
                        );
                    }
                    return (
                        <LegendPill
                            key={node.item.name}
                            item={node.item}
                        />
                    );
                })}
            </div>
        );
    }

    // ── Right legend ──────────────────────────────────────────────────────────
    if (pos === 'right') {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                padding: '2px 0 2px 12px',
                fontFamily: 'inherit',
                alignSelf: 'center',
            }}>
                {items.map((item) => (
                    <LegendPill key={item.name} item={item} />
                ))}
            </div>
        );
    }

    // ── Top legend ────────────────────────────────────────────────────────────
    return (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '3px 10px',
            justifyContent: 'center',
            padding: '2px 0 4px',
            fontFamily: 'inherit',
        }}>
            {items.map((item) => (
                <LegendPill key={item.name} item={item} />
            ))}
        </div>
    );
}

